import React from 'react';
import { toast } from 'react-toastify';
import type { DeepReadonly } from '@itandi/itandi-bb-ui';
import { RiCheckLine, RiErrorWarningLine } from '@itandi/itandi-bb-ui';
import { useOpenHandler } from '~/hooks/useOpenHandler';
import type { VendorPartner } from '~/models/VendorPartner';
import { TableRow } from '~/components/common/parts/table/TableRow';
import { Dropdown } from '~/components/common/parts/dropdown/Dropdown';
import { DropdownList } from '~/components/common/parts/dropdown/DropdownList';
import { DropdownMenu } from '~/components/common/parts/dropdown/DropdownMenu';
import Styles from './VendorPartnerListRow.module.scss';
import { ConfirmModal } from '~/components/common/parts/modal/ConfirmModal';
import { destroy, update } from '~/utils/api/vendors/VendorPartner';
import { isSuccess } from '~/utils/api/api_handler';
import { useVendorPartners } from '~/hooks/vendors/useVendorPartners';

type Props = DeepReadonly<{
  vendorPartner: VendorPartner;
}>;

export const VendorPartnerListRow: React.FC<Props> = ({
  vendorPartner
}: Props) => {
  const { mutate: mutatePartners } = useVendorPartners().index();
  const {
    isOpen: openDeleteConfirm,
    handleOpen: handleOpenDelete,
    handleClose: handleCloseDelete
  } = useOpenHandler();
  const {
    isOpen: openAllowMessageConfirm,
    handleOpen: handleOpenAllowMessage,
    handleClose: handleCloseAllowMessage
  } = useOpenHandler();
  const handleDeleteAccept = React.useCallback(async () => {
    const response = await destroy(vendorPartner.id);
    if (isSuccess(response)) {
      await mutatePartners?.();
      toast.success('協力会社を削除しました。');
      return;
    }
    toast.error('エラーが発生しました');
  }, [mutatePartners, vendorPartner.id]);
  const handleUpdateAllowMessage = React.useCallback(async () => {
    const response = await update(
      vendorPartner.id,
      !vendorPartner.allowMessage
    );
    if (isSuccess(response)) {
      await mutatePartners?.();
      handleCloseAllowMessage();
      toast.success('チャット許可を更新しました。');
      return;
    }
    toast.error('エラーが発生しました');
  }, [
    handleCloseAllowMessage,
    mutatePartners,
    vendorPartner.allowMessage,
    vendorPartner.id
  ]);
  return (
    <TableRow>
      <td>{vendorPartner.partnerVendorCompany.name}</td>
      <td>
        {vendorPartner.allowMessage ? (
          <span className={Styles.AllowMessageLabel}>
            <RiCheckLine className={Styles.CheckIcon} />
            チャット許可
          </span>
        ) : (
          <span className={Styles.AllowMessageLabel}>
            <RiErrorWarningLine className={Styles.AlertIcon} />
            チャット不許可
          </span>
        )}
      </td>
      <td>
        <Dropdown title="操作" variant="normal">
          <DropdownList>
            <DropdownMenu onClick={handleOpenAllowMessage}>
              {vendorPartner.allowMessage
                ? 'チャットを許可しない'
                : 'チャットを許可する'}
            </DropdownMenu>
            <DropdownMenu
              className={Styles.DeleteMenu}
              onClick={handleOpenDelete}
            >
              削除
            </DropdownMenu>
          </DropdownList>
        </Dropdown>
        <ConfirmModal
          confirmType="delete"
          handleAccept={handleDeleteAccept}
          handleCancel={handleCloseDelete}
          isOpen={openDeleteConfirm}
        >
          {`${vendorPartner.partnerVendorCompany.name}を協力会社リストから削除してよろしいですか？`}
        </ConfirmModal>
        <ConfirmModal
          confirmType="save"
          handleAccept={handleUpdateAllowMessage}
          handleCancel={handleCloseAllowMessage}
          isOpen={openAllowMessageConfirm}
        >
          {vendorPartner.allowMessage
            ? `${vendorPartner.partnerVendorCompany.name}のチャットを許可しないようにしますか？`
            : `${vendorPartner.partnerVendorCompany.name}のチャットを許可しますか？`}
        </ConfirmModal>
      </td>
    </TableRow>
  );
};
